/**
 * Plano de leitura da Bíblia em 365 dias, em dois cânones.
 *
 * Os livros, abreviações e quantidades de capítulos são dados factuais; o plano
 * apenas distribui os capítulos em partes iguais ao longo do ano, na ordem
 * canônica — o formato dos planos "Bíblia em um ano".
 *
 * Sobre a contagem de capítulos:
 *
 * - **Evangélica (protestante):** 66 livros, 1.189 capítulos.
 * - **Católica:** 73 livros, 1.334 capítulos. Acrescenta os deuterocanônicos
 *   (Tobias, Judite, Sabedoria, Eclesiástico, Baruc, 1 e 2 Macabeus) e os
 *   capítulos extras de Ester (16) e Daniel (14).
 *
 * A numeração católica segue as edições em português de base vulgata, em que
 * Malaquias tem 4 capítulos e Joel tem 3 — é daí que vem o total de 1.334.
 * Outras edições católicas podem numerar Joel e Malaquias de forma diferente,
 * por isso o total aparece na interface para você conferir contra a sua Bíblia.
 */
/** Livros presentes nos dois cânones, até Neemias. */
const ANTIGO_COMUM = [
    ['Gênesis', 'Gn', 50],
    ['Êxodo', 'Êx', 40],
    ['Levítico', 'Lv', 27],
    ['Números', 'Nm', 36],
    ['Deuteronômio', 'Dt', 34],
    ['Josué', 'Js', 24],
    ['Juízes', 'Jz', 21],
    ['Rute', 'Rt', 4],
    ['1 Samuel', '1Sm', 31],
    ['2 Samuel', '2Sm', 24],
    ['1 Reis', '1Rs', 22],
    ['2 Reis', '2Rs', 25],
    ['1 Crônicas', '1Cr', 29],
    ['2 Crônicas', '2Cr', 36],
    ['Esdras', 'Ed', 10],
    ['Neemias', 'Ne', 13],
];
/** Sapienciais e profetas, na ordem canônica; os deuterocanônicos entram no meio. */
const ANTIGO_POETICOS = [
    ['Jó', 'Jó', 42],
    ['Salmos', 'Sl', 150],
    ['Provérbios', 'Pv', 31],
    ['Eclesiastes', 'Ec', 12],
    ['Cânticos', 'Ct', 8],
];
const ANTIGO_PROFETAS = [
    ['Isaías', 'Is', 66],
    ['Jeremias', 'Jr', 52],
    ['Lamentações', 'Lm', 5],
    ['Ezequiel', 'Ez', 48],
    ['Oseias', 'Os', 14],
    ['Joel', 'Jl', 3],
    ['Amós', 'Am', 9],
    ['Obadias', 'Ob', 1],
    ['Jonas', 'Jn', 4],
    ['Miqueias', 'Mq', 7],
    ['Naum', 'Na', 3],
    ['Habacuque', 'Hc', 3],
    ['Sofonias', 'Sf', 3],
    ['Ageu', 'Ag', 2],
    ['Zacarias', 'Zc', 14],
];
const NOVO_TESTAMENTO = [
    ['Mateus', 'Mt', 28],
    ['Marcos', 'Mc', 16],
    ['Lucas', 'Lc', 24],
    ['João', 'Jo', 21],
    ['Atos', 'At', 28],
    ['Romanos', 'Rm', 16],
    ['1 Coríntios', '1Co', 16],
    ['2 Coríntios', '2Co', 13],
    ['Gálatas', 'Gl', 6],
    ['Efésios', 'Ef', 6],
    ['Filipenses', 'Fp', 4],
    ['Colossenses', 'Cl', 4],
    ['1 Tessalonicenses', '1Ts', 5],
    ['2 Tessalonicenses', '2Ts', 3],
    ['1 Timóteo', '1Tm', 6],
    ['2 Timóteo', '2Tm', 4],
    ['Tito', 'Tt', 3],
    ['Filemom', 'Fm', 1],
    ['Hebreus', 'Hb', 13],
    ['Tiago', 'Tg', 5],
    ['1 Pedro', '1Pe', 5],
    ['2 Pedro', '2Pe', 3],
    ['1 João', '1Jo', 5],
    ['2 João', '2Jo', 1],
    ['3 João', '3Jo', 1],
    ['Judas', 'Jd', 1],
    ['Apocalipse', 'Ap', 22],
];
function montar(definicoes, inicioIndice, testamento) {
    return definicoes.map(([nome, abreviacao, capitulos], posicao) => ({
        indice: inicioIndice + posicao,
        nome,
        abreviacao,
        capitulos,
        testamento,
    }));
}
function antigoEvangelico() {
    return [
        ...ANTIGO_COMUM,
        ['Ester', 'Et', 10],
        ...ANTIGO_POETICOS,
        ...ANTIGO_PROFETAS.slice(0, 3),
        ['Daniel', 'Dn', 12],
        ...ANTIGO_PROFETAS.slice(3),
        ['Malaquias', 'Ml', 4],
    ];
}
function antigoCatolico() {
    // Ordem das edições católicas em português: Tobias, Judite e os Macabeus
    // depois de Neemias; Sabedoria, Eclesiástico e Baruc entre os sapienciais;
    // Ester e Daniel já com os capítulos acrescentados.
    const definicoes = [
        ...ANTIGO_COMUM,
        ['Tobias', 'Tb', 14],
        ['Judite', 'Jt', 16],
        ['Ester', 'Et', 16],
        ['1 Macabeus', '1Mc', 16],
        ['2 Macabeus', '2Mc', 15],
        ...ANTIGO_POETICOS,
        ['Sabedoria', 'Sb', 19],
        ['Eclesiástico', 'Eclo', 51],
        ['Baruc', 'Br', 6],
        ...ANTIGO_PROFETAS.slice(0, 3),
        ['Daniel', 'Dn', 14],
        ...ANTIGO_PROFETAS.slice(3),
        ['Malaquias', 'Ml', 4],
    ];
    return definicoes;
}
export const LIVROS_EVANGELICA = (() => {
    const antigo = antigoEvangelico();
    return [...montar(antigo, 0, 'ANTIGO'), ...montar(NOVO_TESTAMENTO, antigo.length, 'NOVO')];
})();
export const LIVROS_CATOLICA = (() => {
    const antigo = antigoCatolico();
    return [...montar(antigo, 0, 'ANTIGO'), ...montar(NOVO_TESTAMENTO, antigo.length, 'NOVO')];
})();
export function livrosDoCanon(canon) {
    return canon === 'CATOLICA' ? LIVROS_CATOLICA : LIVROS_EVANGELICA;
}
export function totalDeCapitulos(canon) {
    return livrosDoCanon(canon).reduce((soma, livro) => soma + livro.capitulos, 0);
}
export function totalDeLivros(canon) {
    return livrosDoCanon(canon).length;
}
/** Nomes das abreviações usadas no plano, para exibir na interface. */
export function resumoDoCanon(canon) {
    return `${totalDeLivros(canon)} livros · ${totalDeCapitulos(canon)} capítulos`;
}
function segmentosDoIntervalo(livros, inicioGlobal, fimGlobal) {
    const segmentos = [];
    let acumulado = 0;
    for (const livro of livros) {
        const inicioLivro = acumulado;
        const fimLivro = acumulado + livro.capitulos;
        acumulado = fimLivro;
        if (fimGlobal <= inicioLivro || inicioGlobal >= fimLivro)
            continue;
        segmentos.push({
            livro: livro.indice,
            capituloInicial: Math.max(inicioGlobal, inicioLivro) - inicioLivro + 1,
            capituloFinal: Math.min(fimGlobal, fimLivro) - inicioLivro,
        });
    }
    return segmentos;
}
function formatarSegmento(livros, segmento, abreviar) {
    const livro = livros[segmento.livro];
    const nome = abreviar ? livro.abreviacao : livro.nome;
    if (segmento.capituloInicial === segmento.capituloFinal) {
        return `${nome} ${segmento.capituloInicial}`;
    }
    return `${nome} ${segmento.capituloInicial}–${segmento.capituloFinal}`;
}
/**
 * Distribui todos os capítulos do cânon em `dias` leituras consecutivas.
 * Dias que atravessam o fim de um livro listam os dois trechos.
 */
export function planoDeLeitura(dias = 365, canon = 'EVANGELICA') {
    const livros = livrosDoCanon(canon);
    const total = livros.reduce((soma, livro) => soma + livro.capitulos, 0);
    const plano = [];
    for (let dia = 1; dia <= dias; dia += 1) {
        const inicioGlobal = Math.floor(((dia - 1) * total) / dias);
        const fimGlobal = Math.floor((dia * total) / dias);
        const segmentos = segmentosDoIntervalo(livros, inicioGlobal, fimGlobal);
        plano.push({
            dia,
            segmentos,
            texto: segmentos.map((segmento) => formatarSegmento(livros, segmento, false)).join(' · '),
            textoAbreviado: segmentos.map((segmento) => formatarSegmento(livros, segmento, true)).join(' · '),
        });
    }
    return plano;
}
/** Sugestão de reflexão curta para o cabeçalho do plano. */
export const NOTA_PLANO = 'Leia no seu ritmo. Se perder um dia, retome de onde parou — o objetivo é constância, não perfeição.';
//# sourceMappingURL=biblia.js.map